import { Injectable } from '@angular/core';
import { UtilsService } from './utils.service';
import config from '../../config/config.json';
import clientConfig from '../../config/client_config.json';
import statements from '../../config/statements.json';
import { Question, QuestionType } from '../shared/question';
import { ReloadingService } from './reloading.service';
import { Study } from '../shared/types';

@Injectable({
  providedIn: 'root',
})
export class LoadConfigService {
  questions: Question[] = [];
  randomizedTests = null;
  configLoaded = false;
  _clientConfig = JSON.parse(JSON.stringify(clientConfig));
  _config = JSON.parse(JSON.stringify(config));
  userId = 1
  projectId: string = ''

  constructor(
    private utils: UtilsService,
    private reloadingService: ReloadingService
  ) {
    this.loadRandomizedTestsFromSession();
  }

  async setUserID(userId) {
    this.userId = userId
  }

  setProjectId(projectId) {
    this.projectId = projectId
  }

  async setClientConfig(clientConfigJson) {
    this._clientConfig = clientConfigJson;
    this.configLoaded = true;
    //console.log('set this._clientConfig in load-config', this._clientConfig)
    console.log('updated client_config')
  }

  async setConfig(config) {
    this._config = config
    console.log('updated config')
  }

  getConfig() {
    return this._config;
  }

  getClientConfig() {
    //console.log('get this._clientConfig in load-config', this._clientConfig)
    return this._clientConfig;
  }

  getDecisionKeys() {
    return this._config['decisionKeys']['standard']['keys'].slice();
  }

  getKeyLabels(lng: string = this.getDefaultLanguage()) {
    return this._config['decisionKeys']['standard']['labels'][lng].slice();
  }

  getContinueLabel(lng: string = this.getDefaultLanguage()) {
    return this._config['decisionKeys']['standard']['continue']['space'][lng].slice();
  }

  getDecisionKeysAgreement() {
    return this._config['decisionKeys']['agreement']['keys'].slice();
  }

  getKeyLabelsAgreement(lng: string = this.getDefaultLanguage()) {
    return this._config['decisionKeys']['agreement']['labels'][lng].slice();
  }

  getDecisionKeysIAT(test: string) {
    return this._config['decisionKeys'][test]['keys'].slice();
  }

  getKeyLabelsIAT(test: string, lng: string = this.getDefaultLanguage()) {
    return this._config['decisionKeys'][test]['labels'][lng].slice();
  }

  getFadeOutTime(): number {
    return this._config['fadeOutTime'];
  }

  getFadeOutTimeLong(): number {
    return this._config['fadeOutTimeLong'];
  }

  getFadeOutOpacity(): number {
    return this._config['fadeOutOpacity'];
  }

  getCountdownLength() {
    return this._config['countdownLength'];
  }

  getIATQuestionDisplayLength() {
    return this._config['IATQuestionDisplayLength'];
  }

  getFingerPlacementDisplayLength() {
    return this._config['fingerPlacementDisplayLength'];
  }

  getMinimumReactionTime(): number {
    return this._config['minimumReactionTime'];
  }

  getProceedAfter(): number {
    return this._config['proceedAfter'];
  }

  getproceedAfterLong(): number {
    return this._config['proceedAfterLong'];
  }

  getStatements(lng: string) {
    const utils = new UtilsService();
    const statementsCopy = JSON.parse(JSON.stringify(statements[lng]));
    return utils.shuffle(statementsCopy);
  }

  getAllQuestions(lng: string = 'EN'): QuestionType[] {
    return JSON.parse(JSON.stringify(this._clientConfig['questions'][lng]));
  }

  getRawQuestions(lng: string = 'EN', position: number = 0): QuestionType[] {
    const amountOfQuestionnaires = this.getQuestionnaireAmounts(lng);
    if (position >= amountOfQuestionnaires) {
      throw new Error('cannot find questions');
    }
    return JSON.parse(JSON.stringify(this._clientConfig['questions'][lng][position]));
  }

  getQuestionnaireLength(lng: string = 'EN', position: number = 0) {
    return this.getRawQuestions(lng, position).length;
  }

  getQuestionnaireAmounts(lng: string = 'EN'): number {
    const questions = this._clientConfig['questions'][lng];
  
    if(questions) {
      return questions.length;
    } else {
      console.warn(`Property "${lng}" does not exist on client config object.`);
      return 0
    }
  }

  getQuestionnairRepetitions() {
    const questionnaireRepetitions = this._clientConfig['questions']['config'].questionnaireRepetitions
    return questionnaireRepetitions
  }

  /**
   * This function parses client_config.json and returns all information within 'studies'
   * @returns The studies object as JSON
   */
  getStudiesInfo() {
    return this._clientConfig['studies'];
  }

  /**
   * This function returns the individual blocks from client_config.json which can consist of one or multiple studies
   * @returns an array of study blocks which are JSONs
   */
  getStudies() {
    const studyBlocks = this.getStudiesInfo().map((info) => info.block);

    return studyBlocks
  }

  /**
   * This function picks one test within a block of multiple tests, based on the User ID, and returns it as a single element array
   * @param randomBlocks array of all tests in a block with randomBlock = true
   * @returns single element array of the config of a test
   */
  getRandomTest(randomBlocks) {

 /*    // transform ID and number of blocks to numbers
    const intId = +this.userId
    const numberOfBlocks = +randomBlocks.length

    // get block based on ID
    var randomBlockId = intId - 1
    if (intId > numberOfBlocks) {
      randomBlockId = (intId % numberOfBlocks)
    } */

    var numberOfBlocks = +randomBlocks.length;

    var randomBlockId = Math.floor(Math.random() * (numberOfBlocks));

    const test = randomBlocks[randomBlockId].config

    return [test]
  }

  getStudyByStudyTestId(studyTestId: string) {
    const studyBlocks = this.getStudies();
    const studyInfo = studyBlocks.find(
      (test) => test['config']['id'] === studyTestId
    );
    return studyInfo;
  }

  getStudyBlockByIdx(idx: number) {
    return this.getStudies()[idx];
  }

  getStudyByIdx(idx: number) {
    const studies = this.getStudies();
    if (idx > studies.length - 1) {
      throw new Error('not enough studies');
    }

    return studies[idx];
  }

  // randomization of the tests is necessary in order to draw accurate conclusions during the analysis
  getRandomizedStudies(): Study[] {
    // if (this.randomizedTests !== null) {
    //   return this.randomizedTests;
    // }

    this.initRandomizedTests();
    return this.randomizedTests;
  }

  // this should be the default way to get tests
  getRandomizedTestByIdx(idx: number) {
    const studies = this.getRandomizedStudies();
    if (idx > studies.length - 1) {
      return undefined;
    }
    return studies[idx];
  }

  // this should be the default way to get tests
  getRandomizedTestFromTimelineByIdx(idx: number) {
    const studies = this.getRandomizedStudies();
    if (idx > studies.length - 1) {
      return undefined;
    }
    return studies[idx];
  }

  getRandomizedTestConfigFromTimelineByIdx(idx: number) {
    const study = this.getRandomizedTestFromTimelineByIdx(idx);
    return study['config'];
  }

  getRandomizedTestConfigByIdx(idx: number) {
    const study = this.getRandomizedTestByIdx(idx);
    // study?.config -> (study) ? study.config : null; -> if study is not null then study.config else null
    return study;
  }

  getRandomizedTestMediaByIdx(idx: number) {
    const study = this.getRandomizedTestByIdx(idx);
    return study?.media;
  }

  getRandomizedTestMediaPlaceholderByIdx(idx: number) {
    const testMedia = this.getRandomizedTestMediaByIdx(idx);
    return testMedia?.placeholder;
  }

  /**
   * This function extracts the configs from each study block. It was created to make blocks with randomBlock = false/null work
   * @param studyBlock
   * @returns
   */
  private getStudyConfigs(studyBlock) {
    return studyBlock.map(
    (study) => study.config
    )
  }

  /**
   * This function reduces all "random blocks" to a block with one study and returns a clean array of blocks which can be shuffled
   * @param blockArray An array of blocks with one or multiple studies
   * @returns An array of blocks where blocks with randomBlock = true were reduced to one single test in a block
   */
  private getCleanRawStudies(blockArray: any[]) {
    const cleanRawStudies = blockArray.map(
      (studyBlock) => (studyBlock[0].config['randomBlock']) ? this.getRandomTest(studyBlock) : this.getStudyConfigs(studyBlock)
    )
    return cleanRawStudies
  }

  /**
   * This function shuffles all studies within a block where randomBlock != true
   * @param cleanRawStudies
   * @returns An array of blocks where blocks with multiple studies and randomBlock != true were shuffled
   */
  private shuffleBlock(cleanRawStudies: any[]) {
    const shuffledRawStudies = cleanRawStudies.map((studyBlock) =>
      this.utils.shuffle(studyBlock)
    );
    return shuffledRawStudies
  }

    /**
     * This function picks a test within a block and/or shuffles studies within a block and updates the randomizedTests property
     * of the loadConfig class - this.randomizedTests is then an array of the JSON configs of each test in the final order.
     */
    private initRandomizedTests(): void {

      const rawStudies = this.getStudies();
      const cleanRawStudies = this.getCleanRawStudies(rawStudies)
      const shuffledRawStudies = this.shuffleBlock(cleanRawStudies)
      const flattedStudies = shuffledRawStudies.reduce(
        (acc, value) => acc.concat(value),
        []
      );

      this.randomizedTests = flattedStudies;
    }

  getMediaBlock() {
    const studyInfo = this.getStudiesInfo();
    const blockIdx = [];
    for (let i = 0; i < studyInfo.length; i++) {
      const currentBlock = studyInfo[i];

      if (typeof currentBlock['media'] === 'undefined') continue;

      blockIdx.push(i);
    }

    return blockIdx;
  }

  getBlockMediaByIdx(idx: number) {
    const studyInfo = this.getStudiesInfo();
    return studyInfo[idx]['media'];
  }

  getTestAmountBeforeBlock(idx: number) {
    if (idx === 0) return 0;

    const studies = this.getStudies();
    const previousStudies = studies.slice(0, idx);

    const testsBefore = previousStudies.reduce(
      (sum, studyBlock) => sum + studyBlock.length,
      0
    );
    return testsBefore;
  }

  getTestAmountToMediaBlockMapping() {
    const map = {};
    const mediaBlocks = this.getMediaBlock();
    for (const idx of mediaBlocks) {
      const amountOfPreviousTests = this.getTestAmountBeforeBlock(idx);
      map[amountOfPreviousTests] = idx;
    }

    return map;
  }

  private loadRandomizedTestsFromSession() {
    const persistedState = this.reloadingService.getState();
    if (!persistedState) return;

    const persistedStudyOrder = persistedState.config.studyOrder;
    this.randomizedTests = persistedStudyOrder;
  }

  getPracticeBrandNameByStudyTestId(studyTestId: number) {
    return this.getRandomizedTestConfigByIdx(studyTestId)['practiceBrandName'];
  }

  getPracticeBrandImageByStudyTestId(studyTestId: number) {
    return this.getRandomizedTestConfigByIdx(studyTestId)['practiceBrandImage'];
  }

  getBrandNameByStudyTestId(studyTestId: number) {
    return this.getRandomizedTestConfigByIdx(studyTestId)['brandName'];
  }

  getBrandImageByStudyTestId(studyTestId: number) {
    return this.getRandomizedTestConfigByIdx(studyTestId)['brandImage'];
  }

  getProceedAutomaticallyFlag(studyTestId: number) {
    return this.getRandomizedTestConfigByIdx(studyTestId)[
      'proceedAutomatically'
    ];
  }

  getBrandNeeds(lng: string) {
    return this._config['needsphereBrand'][lng];
  }

  getDefaultLanguage() {
    return this._config['defaultLanguage'];
  }

  getIrrelevantSections() {
    return this._config['irrelevantSections'];
  }
}
